import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  Loader2,
  MapPin,
  Truck,
  Search,
  Plus,
  Filter,
  X
} from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import TenantMobileLayout from './TenantMobileLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const TenantMobileJobs = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Get filter from URL params
  const urlFilter = searchParams.get('filter') || 'all';
  const [statusFilter, setStatusFilter] = useState(urlFilter);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Sync URL filter with state
    const filter = searchParams.get('filter');
    if (filter) {
      setStatusFilter(filter);
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jobsRes, customersRes, sitesRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/customers'),
        api.get('/sites')
      ]);
      setJobs(jobsRes.data || []);
      setCustomers(customersRes.data || []);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown';
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '';
  };

  const getDateLabel = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    
    const diffDays = Math.round((selected - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === -1) return 'Yesterday';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    
    return format(selectedDate, 'EEE, d MMM');
  };

  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return today.getTime() === selected.getTime();
  };

  const navigateDate = (direction) => {
    if (direction === 1) {
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      setSelectedDate(subDays(selectedDate, 1));
    }
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getFilteredJobs = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    
    return jobs.filter(job => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getCustomerName(job.customer_id).toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;

      // Status filter
      switch (statusFilter) {
        case 'today':
          const jobStart = job.start_date;
          const jobEnd = job.end_date || job.start_date;
          return today >= jobStart && today <= jobEnd;
        
        case 'unallocated':
          return job.status === 'not_allocated';
        
        case 'allocated':
          return job.status === 'allocated';
        
        case 'confirmed':
          return job.status === 'confirmed';
        
        case 'active':
          return ['not_allocated', 'allocated', 'confirmed'].includes(job.status);
        
        case 'all':
        default:
          // When "all" is selected, filter by selected date
          const start = job.start_date;
          const end = job.end_date || job.start_date;
          return selectedDateStr >= start && selectedDateStr <= end;
      }
    });
  };

  const filteredJobs = getFilteredJobs();

  // Sort jobs: unallocated first, then by time
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    // Unallocated jobs first
    if (a.status === 'not_allocated' && b.status !== 'not_allocated') return -1;
    if (b.status === 'not_allocated' && a.status !== 'not_allocated') return 1;
    
    // Then by start time
    const timeA = a.start_time || '99:99';
    const timeB = b.start_time || '99:99';
    return timeA.localeCompare(timeB);
  });

  const clearFilters = () => {
    setStatusFilter('all');
    setSearchTerm('');
    setSearchParams({});
  };

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter: value });
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'TBD';
    return timeStr.slice(0, 5);
  };

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tenant-mobile')} 
              className="text-gray-400 p-2"
              data-testid="back-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-white font-bold text-lg">Jobs</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 ${showFilters ? 'text-[#ffd108]' : 'text-gray-400'}`}
              data-testid="filter-btn"
            >
              <Filter className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/tenant-mobile/jobs/new')}
              className="text-[#ffd108] p-2"
              data-testid="add-job-btn"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search jobs, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500"
            data-testid="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-3 p-3 bg-[#1a1a1a] rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Status Filter</span>
              {statusFilter !== 'all' && (
                <button 
                  onClick={clearFilters}
                  className="text-[#ffd108] text-xs"
                >
                  Clear
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={handleFilterChange}>
              <SelectTrigger className="bg-[#2a2a2a] border-gray-700 text-white" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2a2a2a] border-gray-700">
                <SelectItem value="all">All (by date)</SelectItem>
                <SelectItem value="today">Today's Jobs</SelectItem>
                <SelectItem value="unallocated">Not Allocated</SelectItem>
                <SelectItem value="allocated">Allocated (To Confirm)</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="active">All Active</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Date Selector - only show when filter is 'all' */}
      {statusFilter === 'all' && (
        <div className="px-4 py-3 bg-[#252525]">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateDate(-1)}
              className="text-gray-400"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              <span className="text-white font-medium">{getDateLabel()}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigateDate(1)}
              className="text-gray-400"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
          {!isToday() && (
            <div className="flex justify-center mt-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={goToToday}
                className="text-[#ffd108] text-xs h-7 px-3"
              >
                <Calendar className="h-3 w-3 mr-1" />
                Today
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Filter Label for non-all filters */}
      {statusFilter !== 'all' && (
        <div className="px-4 py-2 bg-[#252525] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[#ffd108]" />
            <span className="text-white text-sm">
              {statusFilter === 'today' && "Today's Jobs"}
              {statusFilter === 'unallocated' && 'Not Allocated'}
              {statusFilter === 'allocated' && 'To Confirm'}
              {statusFilter === 'confirmed' && 'Confirmed'}
              {statusFilter === 'active' && 'All Active'}
            </span>
            <Badge className="bg-[#ffd108]/20 text-[#ffd108] text-xs">
              {sortedJobs.length}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="text-gray-400 p-1"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Jobs List */}
      <div className="p-4 pb-24">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : sortedJobs.length === 0 ? (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="py-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">
                {searchTerm ? 'No matching jobs found' : 'No jobs for this date'}
              </p>
              <Button 
                className="mt-4 btn-volt"
                onClick={() => navigate('/tenant-mobile/jobs/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Job
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedJobs.map((job) => {
              const isMultiDay = job.end_date && job.end_date !== job.start_date;
              
              return (
                <Card 
                  key={job.id}
                  className="bg-[#2a2a2a] border-gray-700 cursor-pointer hover:border-[#ffd108] transition-colors"
                  onClick={() => navigate(`/tenant-mobile/jobs/${job.id}`)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                          <span className="text-[#ffd108] font-bold">
                            {formatTime(job.start_time)}
                          </span>
                          <StatusBadge status={job.status} />
                          {isMultiDay && (
                            <Badge className="bg-purple-900 text-purple-300 text-xs">
                              Multi-day
                            </Badge>
                          )}
                        </div>
                        <h3 className="text-white font-medium text-lg truncate">
                          {getCustomerName(job.customer_id)}
                        </h3>
                        {job.site_id && getSiteName(job.site_id) && (
                          <p className="text-gray-400 text-sm flex items-center gap-1 mt-1">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{getSiteName(job.site_id)}</span>
                          </p>
                        )}
                        {job.equipment_type && (
                          <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                            <Truck className="h-3 w-3 flex-shrink-0" />
                            {job.equipment_type}
                          </p>
                        )}
                        {job.description && (
                          <p className="text-gray-500 text-xs mt-2 line-clamp-2">{job.description}</p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </TenantMobileLayout>
  );
};

// Status badge component
const StatusBadge = ({ status }) => {
  // Status colors use CSS variables that change based on theme
  const baseClass = "border text-xs";
  switch (status) {
    case 'not_allocated':
      return <Badge variant="secondary" className={baseClass} style={{ backgroundColor: 'var(--status-not-allocated-bg)', color: 'var(--status-not-allocated-text)', borderColor: 'var(--status-not-allocated-border)' }}>Not Allocated</Badge>;
    case 'allocated':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-allocated-bg)', color: 'var(--status-allocated-text)', borderColor: 'var(--status-allocated-border)' }}>Allocated</Badge>;
    case 'confirmed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-confirmed-bg)', color: 'var(--status-confirmed-text)', borderColor: 'var(--status-confirmed-border)' }}>Confirmed</Badge>;
    case 'docketed':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-docketed-bg)', color: 'var(--status-docketed-text)', borderColor: 'var(--status-docketed-border)' }}>Docketed</Badge>;
    case 'invoiced':
      return <Badge className={baseClass} style={{ backgroundColor: 'var(--status-invoiced-bg)', color: 'var(--status-invoiced-text)', borderColor: 'var(--status-invoiced-border)' }}>Invoiced</Badge>;
    default:
      return <Badge variant="secondary" className="text-xs">{status}</Badge>;
  }
};

export default TenantMobileJobs;
